import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ErrorIcon from "@mui/icons-material/Error";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ShieldIcon from "@mui/icons-material/Shield";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

import FileDialog from "components/FileDialog";
import ComunicacionesApi from "api/comunicacionesApi";

const DialogDestinatarios = ({ open, onClose, comunicacion }) => {
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfTitle, setPdfTitle] = useState("");
  const [loading, setLoading] = useState(false);

  if (!comunicacion) return null;

  // Función para generar y visualizar PDF personalizado por destinatario
  const handleViewPdfDestinatario = async (destinatario, destinatarioTipo) => {
    if (!comunicacion) return;

    // Limpiar URL anterior si existe
    if (pdfUrl && pdfUrl.startsWith("blob:")) {
      URL.revokeObjectURL(pdfUrl);
    }

    try {
      setLoading(true);
      const timestamp = Date.now();
      const destinatarioNombre = destinatario.nombre_completo || destinatario.nombre || "";
      const destinatarioId = destinatario.id || null;

      // Mapear el tipo de destinatario al valor técnico para el backend
      let tipoDestinatarioBackend = null;
      if (comunicacion.formato_comunicacion === "notificacionAdmision") {
        // Solo enviar tipo específico para notificacionAdmision
        const tipoMapping = {
          "Central de Riesgo": "central_riesgo",
          Juzgado: "juzgado",
          "Pagador de Libranza": "pagador_libranza",
        };
        tipoDestinatarioBackend = tipoMapping[destinatarioTipo] || null;
      }

      const pdfResponse = await ComunicacionesApi.generateComunicacionPdf(
        comunicacion.id,
        { responseType: "blob" },
        timestamp,
        destinatarioNombre,
        destinatarioId,
        tipoDestinatarioBackend
      );

      const pdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      const uniqueUrl = `${url}#${timestamp}`;

      setPdfTitle(`Notificación para ${destinatarioNombre} - ${destinatarioTipo}`);
      setPdfUrl(uniqueUrl);
      setShowPdfViewer(true);
    } catch (error) {
      console.error("Error generando PDF de notificación:", error);
      alert(obtenerMensajeError(error, "Error al generar el PDF de notificación"));
    } finally {
      setLoading(false);
    }
  };

  // Función helper para obtener correos concatenados
  const getCorreosConcatenados = (correosList) => {
    if (!correosList || !Array.isArray(correosList) || correosList.length === 0) {
      return "No especificado";
    }
    return correosList.map((correo) => correo.email || "Sin email").join(", ");
  };

  // Función para obtener el estado de un correo individual
  const getCorreoStatus = (correo) => {
    if (!correo) {
      return {
        icon: <HourglassEmptyIcon />,
        color: "warning",
        tooltip: "Sin información de correo",
      };
    }

    // Si notificado es true y error_envio es null, se envió correctamente
    if (correo.notificado && !correo.error_envio) {
      return {
        icon: <CheckCircleIcon />,
        color: "success",
        tooltip: "Enviado correctamente",
      };
    }

    // Si notificado es false y error_envio tiene valor, hubo un error
    if (!correo.notificado && correo.error_envio) {
      // Determinar el mensaje según el tipo de error
      let mensajeError = "Error de envío";
      if (correo.error_envio === "rebote") {
        mensajeError = "Rebote: el email no existe o fue rechazado";
      } else if (correo.error_envio === "invalido") {
        mensajeError = "Email inválido";
      } else if (correo.error_envio === "bloqueado") {
        mensajeError = "Email bloqueado";
      } else if (correo.error_envio === "temporal") {
        mensajeError = "Error temporal de envío";
      } else if (correo.error_envio === "spam") {
        mensajeError = "Marcado como spam";
      } else {
        mensajeError = `Error: ${correo.error_envio}`;
      }

      return {
        icon: <ErrorIcon />,
        color: "error",
        tooltip: mensajeError,
      };
    }

    // Si notificado es false y error_envio es null, está pendiente
    if (!correo.notificado && !correo.error_envio) {
      return {
        icon: <HourglassEmptyIcon />,
        color: "warning",
        tooltip: "Pendiente de envío",
      };
    }

    // Caso por defecto
    return {
      icon: <HourglassEmptyIcon />,
      color: "warning",
      tooltip: "Estado desconocido",
    };
  };

  // Función para obtener el estado consolidado de un destinatario basado en todos sus correos
  const getDestinatarioConsolidadoStatus = (destinatario) => {
    if (!destinatario) {
      return {
        icon: <HourglassEmptyIcon />,
        color: "warning",
        tooltip: "Sin información",
      };
    }

    // Para destinatarios con un solo email directo (deudor, centrales de riesgo, etc.)
    if (destinatario.email !== undefined) {
      return getCorreoStatus(destinatario);
    }

    // Para destinatarios con múltiples correos (acreedores)
    if (destinatario.correos && Array.isArray(destinatario.correos)) {
      const correos = destinatario.correos;
      const totalCorreos = correos.length;
      const correosNotificados = correos.filter(
        (correo) => correo.notificado && !correo.error_envio
      ).length;
      const correosConError = correos.filter(
        (correo) => !correo.notificado && correo.error_envio
      ).length;

      // Si todos los correos están notificados correctamente
      if (correosNotificados === totalCorreos) {
        return {
          icon: <CheckCircleIcon />,
          color: "success",
          tooltip: `Todos los correos enviados (${totalCorreos})`,
        };
      }

      // Si algunos correos tienen errores
      if (correosConError > 0) {
        return {
          icon: <ErrorIcon />,
          color: "error",
          tooltip: `${correosConError} de ${totalCorreos} correos con errores`,
        };
      }

      // Si algunos están notificados pero otros están pendientes
      if (correosNotificados > 0) {
        return {
          icon: <HourglassEmptyIcon />,
          color: "warning",
          tooltip: `${correosNotificados} de ${totalCorreos} correos enviados`,
        };
      }

      // Todos están pendientes
      return {
        icon: <HourglassEmptyIcon />,
        color: "warning",
        tooltip: `${totalCorreos} correos pendientes de envío`,
      };
    }

    // Fallback para el campo notificado directo (compatibilidad)
    if (destinatario.notificado) {
      return {
        icon: <CheckCircleIcon />,
        color: "success",
        tooltip: "Notificado correctamente",
      };
    }

    return {
      icon: <HourglassEmptyIcon />,
      color: "warning",
      tooltip: "Pendiente de notificación",
    };
  };

  const renderDestinatarios = () => {
    const destinatarios = comunicacion.destinatarios || {};

    // Para notificacionManual con estructura de lista
    if (
      comunicacion.formato_comunicacion === "notificacionManual" &&
      Array.isArray(destinatarios)
    ) {
      return (
        <>
          <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold", mb: 2 }}>
            Visualización de Destinatarios
          </Typography>
          <List disablePadding>
            {destinatarios.map((destinatario, index) => (
              <ListItem
                key={`destinatario-${index}`}
                secondaryAction={
                  <Box display="flex" alignItems="center">
                    {(() => {
                      const status = getDestinatarioConsolidadoStatus(destinatario);
                      return (
                        <Chip
                          icon={status.icon}
                          color={status.color}
                          size="large"
                          sx={{ mr: 1 }}
                          title={status.tooltip}
                        />
                      );
                    })()}

                    <Tooltip title="Ver/Generar Notificación">
                      <IconButton
                        size="large"
                        color="primary"
                        onClick={() =>
                          handleViewPdfDestinatario(destinatario, "Notificación Manual")
                        }
                        disabled={loading}
                      >
                        <PictureAsPdfIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <ListItemIcon>
                  <PersonIcon fontSize="large" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center">
                      <Typography variant="body1" component="span" fontWeight="medium">
                        {destinatario.nombre_completo || "Sin nombre"}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box component="span">
                      <Typography variant="body2" component="span">
                        {destinatario.email || "No especificado"}
                      </Typography>
                      {/* {destinatario.pdf_url && (
                        <>
                          <br />
                          <Typography
                            variant="body2"
                            component="span"
                            color="primary"
                            sx={{ cursor: "pointer", textDecoration: "underline" }}
                            onClick={() =>
                              handleViewPdf(
                                destinatario.pdf_url,
                                `Comprobante de notificación - ${
                                  destinatario.nombre_completo || "destinatario"
                                }`
                              )
                            }
                          >
                            Ver comprobante de notificación
                          </Typography>
                        </>
                      )} */}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </>
      );
    }

    // Si es una comunicación física (ahora con array de destinatarios)
    if (comunicacion.tipo_comunicacion === "FISICA" && Array.isArray(destinatarios)) {
      return (
        <>
          <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold", mb: 2 }}>
            Destinatarios de Envío Físico
          </Typography>
          <List disablePadding>
            {destinatarios.map((destinatario, index) => (
              <ListItem
                key={`fisica-${index}-${destinatario.id || index}`}
                secondaryAction={
                  <Box display="flex" alignItems="center">
                    {(() => {
                      const status = getDestinatarioConsolidadoStatus(destinatario);
                      return (
                        <Chip
                          icon={status.icon}
                          color={status.color}
                          size="large"
                          sx={{ mr: 1 }}
                          title={status.tooltip}
                        />
                      );
                    })()}

                    <Tooltip title="Ver/Generar Notificación">
                      <IconButton
                        size="large"
                        color="primary"
                        onClick={() =>
                          handleViewPdfDestinatario(destinatario, "Notificación Física")
                        }
                        disabled={loading}
                      >
                        <PictureAsPdfIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <ListItemIcon>
                  <BusinessIcon fontSize="large" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center">
                      <Typography variant="body1" component="span" fontWeight="medium">
                        {destinatario.nombre || "Sin nombre"}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box component="span">
                      <Typography variant="body2" component="span">
                        Dirección: {destinatario.direccion || "No especificada"}
                      </Typography>
                      <br />
                      <Typography variant="body2" component="span">
                        Ciudad: {destinatario.ciudad || "No especificada"}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </>
      );
    }

    // Para comunicaciones por EMAIL
    return (
      <>
        {/* Deudor */}
        {destinatarios.deudor && (
          <>
            <Grid container spacing={1}>
              <Grid size={6}>
                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold" }}>
                  Deudor
                </Typography>
                <List disablePadding>
                  <ListItem
                    secondaryAction={
                      <Box display="flex" alignItems="center">
                        {(() => {
                          const status = getDestinatarioConsolidadoStatus(destinatarios.deudor);
                          return (
                            <Chip
                              icon={status.icon}
                              color={status.color}
                              size="large"
                              sx={{ mr: 1 }}
                              title={status.tooltip}
                            />
                          );
                        })()}
                        <Tooltip title="Ver/Generar Notificación">
                          <IconButton
                            size="large"
                            color="primary"
                            onClick={() =>
                              handleViewPdfDestinatario(destinatarios.deudor, "Deudor")
                            }
                            disabled={loading}
                          >
                            <PictureAsPdfIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemIcon>
                      <PersonIcon fontSize="large" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body1" component="span" fontWeight="medium">
                            {destinatarios.deudor.nombre_completo || "Sin nombre"}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box component="span">
                          <Typography variant="body2" component="span">
                            {destinatarios.deudor.email || "No especificado"}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid size={6}>
                {/* Apoderado Deudor */}
                {destinatarios.deudor_apoderado && (
                  <>
                    <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold" }}>
                      Apoderado del Deudor
                    </Typography>
                    <List disablePadding>
                      <ListItem
                        secondaryAction={
                          <Box display="flex" alignItems="center">
                            {(() => {
                              const status = getDestinatarioConsolidadoStatus(
                                destinatarios.deudor_apoderado
                              );
                              return (
                                <Chip
                                  icon={status.icon}
                                  color={status.color}
                                  size="large"
                                  sx={{ mr: 1 }}
                                  title={status.tooltip}
                                />
                              );
                            })()}

                            <Tooltip title="Ver/Generar Notificación">
                              <IconButton
                                size="large"
                                color="primary"
                                onClick={() =>
                                  handleViewPdfDestinatario(
                                    destinatarios.deudor_apoderado,
                                    "Apoderado del Deudor"
                                  )
                                }
                                disabled={loading}
                              >
                                <PictureAsPdfIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        }
                      >
                        <ListItemIcon>
                          <ShieldIcon fontSize="large" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center">
                              <Typography variant="body1" component="span" fontWeight="medium">
                                {destinatarios.deudor_apoderado.nombre_completo || "Sin nombre"}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box component="span">
                              <Typography variant="body2" component="span">
                                {destinatarios.deudor_apoderado.email || "No especificado"}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    </List>
                  </>
                )}
              </Grid>
            </Grid>
          </>
        )}
        {/* Acreedores */}
        {destinatarios.acreedores && destinatarios.acreedores.length > 0 && (
          <>
            <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold" }}>
              Acreedores y Apoderados
            </Typography>
            <List disablePadding>
              {destinatarios.acreedores.map((acreedor, index) => (
                <Box key={`acreedor-${index}-${acreedor.id || index}`} sx={{ mb: 1 }}>
                  <Grid container spacing={1}>
                    {/* Acreedor Principal */}
                    <Grid size={acreedor.apoderado ? 6 : 12}>
                      <ListItem
                        secondaryAction={
                          <Box display="flex" alignItems="center">
                            {(() => {
                              const status = getDestinatarioConsolidadoStatus(acreedor);
                              return (
                                <Chip
                                  icon={status.icon}
                                  color={status.color}
                                  size="large"
                                  sx={{ mr: 1 }}
                                  title={status.tooltip}
                                />
                              );
                            })()}
                            <Tooltip title="Ver/Generar Notificación">
                              <IconButton
                                size="large"
                                color="primary"
                                onClick={() => handleViewPdfDestinatario(acreedor, "Acreedor")}
                                disabled={loading}
                              >
                                <PictureAsPdfIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        }
                      >
                        <ListItemIcon>
                          <BusinessIcon fontSize="large" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center">
                              <Typography variant="body1" fontWeight="medium">
                                {acreedor.nombre_completo || "Acreedor sin nombre"}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box component="span">
                              <Typography variant="body2" component="span">
                                {acreedor.correos
                                  ? getCorreosConcatenados(acreedor.correos)
                                  : acreedor.email || "No especificado"}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    </Grid>

                    {/* Apoderado del acreedor */}
                    {acreedor.apoderado && (
                      <Grid size={6}>
                        <ListItem
                          secondaryAction={
                            <Box display="flex" alignItems="center">
                              {(() => {
                                const status = getDestinatarioConsolidadoStatus(acreedor.apoderado);
                                return (
                                  <Chip
                                    icon={status.icon}
                                    color={status.color}
                                    size="large"
                                    sx={{ mr: 1 }}
                                    title={status.tooltip}
                                  />
                                );
                              })()}
                              <Tooltip title="Ver/Generar Notificación">
                                <IconButton
                                  size="large"
                                  color="primary"
                                  onClick={() =>
                                    handleViewPdfDestinatario(
                                      acreedor.apoderado,
                                      "Apoderado de Acreedor"
                                    )
                                  }
                                  disabled={loading}
                                >
                                  <PictureAsPdfIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          }
                        >
                          <ListItemIcon>
                            <BusinessIcon fontSize="large" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center">
                                <Typography variant="body1" fontWeight="medium">
                                  {acreedor.apoderado.nombre_completo || "Apoderado sin nombre"}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box component="span">
                                <Typography variant="body2" component="span">
                                  {acreedor.apoderado.email || "No especificado"}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ))}
            </List>
          </>
        )}
        {/* Centrales de Riesgo */}
        {destinatarios.centrales_riesgo && destinatarios.centrales_riesgo.length > 0 && (
          <>
            <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold" }}>
              Centrales de Riesgo
            </Typography>
            <List disablePadding>
              {destinatarios.centrales_riesgo.map((central, index) => (
                <ListItem
                  key={`central-${index}`}
                  secondaryAction={
                    <Box display="flex" alignItems="center">
                      {(() => {
                        const status = getDestinatarioConsolidadoStatus(central);
                        return (
                          <Chip
                            icon={status.icon}
                            color={status.color}
                            size="large"
                            sx={{ mr: 1 }}
                            title={status.tooltip}
                          />
                        );
                      })()}
                      <Tooltip title="Ver/Generar Notificación">
                        <IconButton
                          size="large"
                          color="primary"
                          onClick={() => handleViewPdfDestinatario(central, "Central de Riesgo")}
                          disabled={loading}
                        >
                          <PictureAsPdfIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemIcon>
                    <AccountBalanceIcon fontSize="large" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <Typography variant="body1" component="span" fontWeight="medium">
                          {central.nombre || "Central sin nombre"}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box component="span">
                        <Typography variant="body2" component="span">
                          {central.email || "No especificado"}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
        {/* Juzgados */}
        {destinatarios.juzgados && destinatarios.juzgados.length > 0 && (
          <>
            <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold" }}>
              Juzgados
            </Typography>
            <List disablePadding>
              {destinatarios.juzgados.map((juzgado, index) => (
                <ListItem
                  key={`juzgado-${index}`}
                  secondaryAction={
                    <Box display="flex" alignItems="center">
                      {(() => {
                        const status = getDestinatarioConsolidadoStatus(juzgado);
                        return (
                          <Chip
                            icon={status.icon}
                            color={status.color}
                            size="large"
                            sx={{ mr: 1 }}
                            title={status.tooltip}
                          />
                        );
                      })()}
                      <Tooltip title="Ver/Generar Notificación">
                        <IconButton
                          size="large"
                          color="primary"
                          onClick={() => handleViewPdfDestinatario(juzgado, "Juzgado")}
                          disabled={loading}
                        >
                          <PictureAsPdfIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemIcon>
                    <AccountBalanceIcon fontSize="large" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <Typography variant="body1" component="span" fontWeight="medium">
                          {juzgado.nombre || "Juzgado sin nombre"}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box component="span">
                        <Typography variant="body2" component="span">
                          {juzgado.email || "No especificado"}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
        {/* Pagadores de Libranza */}
        {destinatarios.pagadores_libranza && destinatarios.pagadores_libranza.length > 0 && (
          <>
            <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold" }}>
              Pagadores de Libranza
            </Typography>
            <List disablePadding>
              {destinatarios.pagadores_libranza.map((pagador, index) => (
                <ListItem
                  key={`pagador-${index}`}
                  secondaryAction={
                    <Box display="flex" alignItems="center">
                      {(() => {
                        const status = getDestinatarioConsolidadoStatus(pagador);
                        return (
                          <Chip
                            icon={status.icon}
                            color={status.color}
                            size="large"
                            sx={{ mr: 1 }}
                            title={status.tooltip}
                          />
                        );
                      })()}
                      <Tooltip title="Ver/Generar Notificación">
                        <IconButton
                          size="large"
                          color="primary"
                          onClick={() => handleViewPdfDestinatario(pagador, "Pagador de Libranza")}
                          disabled={loading}
                        >
                          <PictureAsPdfIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemIcon>
                    <AccountBalanceIcon fontSize="large" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <Typography variant="body1" component="span" fontWeight="medium">
                          {pagador.nombre_completo || "Pagador sin nombre"}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box component="span">
                        <Typography variant="body2" component="span">
                          {pagador.email || "No especificado"}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </>
    );
  };

  // Verificar si todos los destinatarios han sido notificados
  const checkAllNotified = () => {
    const { destinatarios } = comunicacion || {};
    if (!destinatarios) return false;

    // Para notificacionManual con estructura de lista
    if (
      comunicacion.formato_comunicacion === "notificacionManual" &&
      Array.isArray(destinatarios)
    ) {
      return destinatarios.every((destinatario) => destinatario.notificado);
    }

    // Para comunicación física con array de destinatarios
    if (comunicacion.tipo_comunicacion === "FISICA" && Array.isArray(destinatarios)) {
      return destinatarios.every((destinatario) => destinatario.notificado);
    }

    // Verificar deudor
    if (destinatarios.deudor && !destinatarios.deudor.notificado) return false;

    // Verificar apoderado del deudor
    if (destinatarios.deudor_apoderado && !destinatarios.deudor_apoderado.notificado) return false;

    // Verificar centrales de riesgo
    if (destinatarios.centrales_riesgo && destinatarios.centrales_riesgo.length > 0) {
      if (!destinatarios.centrales_riesgo.every((central) => central.notificado)) return false;
    }

    // Verificar acreedores
    if (destinatarios.acreedores && destinatarios.acreedores.length > 0) {
      if (!destinatarios.acreedores.every((acreedor) => acreedor.notificado)) return false;
    }

    return true;
  };

  return (
    <>
      {loading && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
        <DialogTitle>
          <Typography variant="h4" component="div">
            Visualización de Destinatarios
          </Typography>
          {comunicacion.tipo_comunicacion && (
            <Box display="flex" alignItems="center" mt={1} justifyContent="space-between">
              <Box display="flex" alignItems="center">
                {comunicacion.tipo_comunicacion === "EMAIL" ? (
                  <Chip
                    icon={<EmailIcon fontSize="small" />}
                    label="Email"
                    size="large"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                ) : (
                  <Chip
                    icon={<LocalShippingIcon fontSize="small" />}
                    label="Física"
                    size="large"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
                <Typography variant="body1" color="textSecondary" sx={{ ml: 2 }}>
                  {comunicacion.formato_comunicacion}
                </Typography>
              </Box>

              {/* Mostrar un indicador general si todos los destinatarios están notificados */}
              <Chip
                icon={
                  checkAllNotified() ? (
                    <CheckCircleIcon fontSize="small" />
                  ) : (
                    <HourglassEmptyIcon fontSize="small" />
                  )
                }
                label={checkAllNotified() ? "Todos notificados" : "Notificación pendiente"}
                color={checkAllNotified() ? "success" : "warning"}
                size="medium"
              />
            </Box>
          )}
        </DialogTitle>
        <DialogContent dividers>{renderDestinatarios()}</DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visor de PDF usando FileDialog */}
      <FileDialog
        open={showPdfViewer}
        onClose={() => {
          setShowPdfViewer(false);
          // Limpiar URL del blob si existe
          if (pdfUrl && pdfUrl.startsWith("blob:")) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl("");
          }
        }}
        title={pdfTitle}
        content={
          <iframe
            src={pdfUrl}
            width="100%"
            height="500px"
            style={{ border: "none" }}
            key={pdfUrl}
          />
        }
      />
    </>
  );
};

DialogDestinatarios.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  comunicacion: PropTypes.object,
};

export default DialogDestinatarios;
